import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import { serializeRenderedMarkdown, writeClipboardPayload } from './copyRenderedMarkdown'

interface CopyButtonProps {
  /**
   * Fallback plain-text payload when `source` is omitted or the rendered
   * node is empty (ids, one-liners, still-mounting markdown).
   */
  text: string
  /**
   * Live rendered node to copy. When it has visible content, that wins
   * over `text` so chat copy matches the answer on screen, not markdown
   * source.
   */
  source?: () => HTMLElement | null
  /** Accessible label / tooltip; defaults to a generic copy hint. */
  label?: string
  className?: string
}

/**
 * Small ghost icon-button that copies to the clipboard. Swaps the
 * classic copy glyph for a check mark for ~2s after a successful copy so
 * the click registers visually. Falls back silently when the Clipboard
 * API is unavailable (e.g. non-secure contexts) so it never throws in the
 * chat render path.
 */
export function CopyButton({
  text,
  source,
  label = 'Copy to clipboard',
  className
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    },
    []
  )

  const handleCopy = useCallback(async () => {
    try {
      const rendered = source?.()
      const fromDom = rendered ? serializeRenderedMarkdown(rendered) : null
      const payload = fromDom !== null && fromDom.text.length > 0 ? fromDom : { text }
      if (payload.text.length === 0) return
      await writeClipboardPayload(payload)
      setCopied(true)
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Swallow — copying is a best-effort affordance and must never break
      // the surrounding chat render.
    }
  }, [source, text])

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
      onClick={handleCopy}
      className={cn('text-muted-foreground hover:text-foreground', className)}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  )
}
